import { ApiShopperLandingPageLink } from '@storefronts/common-types'
import { AnalyticsContext } from '../context/analyticsContext'
import {
    createContext,
    useState,
    useEffect,
    useContext,
    useMemo,
    useCallback,
    useRef,
} from 'react'
import { CartContext } from '../context/cartContext'

/**
 * Registration info for an Inline Product section on the storefront.
 * Used to enable scrolling to the section when the user clicks on the
 * product name or variant in the floating Buy Now button.
 */
type InlineProductSectionRegistration = {
    element: HTMLElement
    linkHash: string
}

export type ActiveLPLinkOptions = {
    /** Shopify variant id when opening PDP (e.g. PLP swatch / product card) */
    initialVariantShopifyId?: string | null
    /** When true, if the initial/default variant is unavailable, prefer first available (optionally matching preferred option) */
    preferAvailable?: boolean
    /** Option name to prefer when resolving an available variant (e.g. "Color" for swatch); value is derived from the chosen variant */
    preferredOptionName?: string | null
}

export type LastOpenedOverlay = 'cart' | 'pdp' | null

type PageContext = {
    isGridExpanded: boolean
    setIsGridExpanded: React.Dispatch<React.SetStateAction<boolean>>
    isCartOpen: boolean
    setIsCartOpen: (open: boolean) => void
    activeLPLink: ApiShopperLandingPageLink | null
    /** Variant to show when opening full PDP from PLP (cleared when link is null) */
    activeLPLinkInitialVariantShopifyId: string | null
    activeLPLinkBodyBadgesOverride: string[] | undefined
    setActiveLPLink: (
        link: ApiShopperLandingPageLink | null,
        options?: ActiveLPLinkOptions,
        bodyBadgesOverride?: string[],
    ) => void
    /** Tracks which overlay (cart or pdp) was opened most recently, for z-index layering */
    lastOpenedOverlay: LastOpenedOverlay
    registerCartActionButton: (element: HTMLElement | null) => () => void
    hasVisibleCartActionButtons: boolean
    selectedCartActionVariantShopifyId: string | null
    setSelectedCartActionVariantShopifyId: React.Dispatch<
        React.SetStateAction<string | null>
    >
    /**
     * Register an Inline Product section with its associated linkHash.
     * Returns an unregister function to be called on unmount.
     */
    registerInlineProductSection: (
        element: HTMLElement | null,
        linkHash: string,
    ) => () => void
    /**
     * Scroll to the nearest Inline Product section that matches the given linkHash.
     * Returns true if a matching section was found and scrolled to, false otherwise.
     */
    scrollToInlineProductSection: (linkHash: string) => boolean
    /**
     * Set when a collections tile is opened: the PLPDrawer renders these
     * products (instead of all landing page links). Cleared automatically
     * whenever the grid closes (any close path).
     */
    collectionOverrideLinks: ApiShopperLandingPageLink[] | null
    /**
     * Opens the PLP drawer scoped to a specific collection's products. Use this
     * (not `setIsGridExpanded`) when opening from a collections tile.
     */
    openCollectionGrid: (links: ApiShopperLandingPageLink[]) => void
}

const DefaultPageContext: PageContext = {
    isGridExpanded: false,
    setIsGridExpanded: () => {},
    isCartOpen: false,
    setIsCartOpen: () => {},
    activeLPLink: null,
    activeLPLinkInitialVariantShopifyId: null,
    activeLPLinkBodyBadgesOverride: undefined,
    setActiveLPLink: () => {},
    lastOpenedOverlay: null,
    registerCartActionButton: () => () => {},
    hasVisibleCartActionButtons: false,
    selectedCartActionVariantShopifyId: null,
    setSelectedCartActionVariantShopifyId: () => {},
    registerInlineProductSection: () => () => {},
    scrollToInlineProductSection: () => false,
    collectionOverrideLinks: null,
    openCollectionGrid: () => {},
}

export const PageContext = createContext<PageContext>(DefaultPageContext)

/**
 * The floating CTA hides while an inline cart button is on screen. A button
 * at the viewport edge can flip in and out on small layout shifts; requiring
 * its visibility to hold this long keeps those flips from toggling the CTA.
 */
export const CART_ACTION_VISIBILITY_SETTLE_MS = 150

export const PageContextProvider: React.FC<{ children: React.ReactNode }> = ({
    children,
}) => {
    const { Analytics } = useContext(AnalyticsContext)
    const { cart } = useContext(CartContext)

    const [isGridExpanded, setIsGridExpandedState] = useState<boolean>(false)
    const [collectionOverrideLinks, setCollectionOverrideLinks] = useState<
        ApiShopperLandingPageLink[] | null
    >(null)
    const [isCartOpen, setIsCartOpenState] = useState<boolean>(false)

    // Generic grid toggle: any open/close through this path (header close,
    // "view all products" CTA, etc.) clears the collection override so the
    // drawer shows the full catalog. Opening scoped to a collection must go
    // through `openCollectionGrid`, which sets the override directly.
    const setIsGridExpanded = useCallback<
        React.Dispatch<React.SetStateAction<boolean>>
    >((action) => {
        setIsGridExpandedState(action)
        setCollectionOverrideLinks(null)
    }, [])

    const openCollectionGrid = useCallback(
        (links: ApiShopperLandingPageLink[]) => {
            setCollectionOverrideLinks(links)
            setIsGridExpandedState(true)
        },
        [],
    )
    const [activeLPLink, setActiveLPLinkState] =
        useState<ApiShopperLandingPageLink | null>(null)
    const [
        activeLPLinkInitialVariantShopifyId,
        setActiveLPLinkInitialVariantShopifyId,
    ] = useState<string | null>(null)
    const [activeLPLinkBodyBadgesOverride, setActiveLPLinkBodyBadgesOverride] =
        useState<string[] | undefined>(undefined)
    const [lastOpenedOverlay, setLastOpenedOverlay] =
        useState<LastOpenedOverlay>(null)

    const setIsCartOpen = useCallback((open: boolean) => {
        setIsCartOpenState(open)
        if (open) {
            setLastOpenedOverlay('cart')
        }
    }, [])

    const setActiveLPLink = useCallback(
        (
            link: ApiShopperLandingPageLink | null,
            options?: ActiveLPLinkOptions,
            bodyBadgesOverride?: string[],
        ) => {
            setActiveLPLinkState(link)
            if (link === null) {
                setActiveLPLinkInitialVariantShopifyId(null)
                setActiveLPLinkBodyBadgesOverride(undefined)
            } else {
                setActiveLPLinkInitialVariantShopifyId(
                    options?.initialVariantShopifyId ?? null,
                )
                setActiveLPLinkBodyBadgesOverride(bodyBadgesOverride)
                setLastOpenedOverlay('pdp')
            }
        },
        [],
    )
    const [visibleCartActionButtons, setVisibleCartActionButtons] = useState<
        Set<HTMLElement>
    >(new Set())

    const [
        selectedCartActionVariantShopifyId,
        setSelectedCartActionVariantShopifyId,
    ] = useState<string | null>(null)

    // Track registered Inline Product sections for scroll-to functionality
    const inlineProductSectionsRef = useRef<
        Map<HTMLElement, InlineProductSectionRegistration>
    >(new Map())

    useEffect(() => {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (isGridExpanded) {
            Analytics.TRACK_PLP_PAGEVIEW()
        }
    }, [isGridExpanded])

    useEffect(() => {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (isCartOpen) {
            Analytics.TRACK_CART_PAGEVIEW(cart)
        }
    }, [isCartOpen])

    useEffect(() => {
        // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
        if (activeLPLink) {
            Analytics.TRACK_PDP_PAGEVIEW(activeLPLink)
        }
    }, [activeLPLink])

    const registerCartActionButton = useCallback(
        (element: HTMLElement | null) => {
            if (!element) {
                return () => {}
            }

            let settleTimer: ReturnType<typeof setTimeout> | undefined

            const commitVisibility = (isVisible: boolean) => {
                setVisibleCartActionButtons((prev) => {
                    if (prev.has(element) === isVisible) return prev
                    const next = new Set(prev)
                    if (isVisible) {
                        next.add(element)
                    } else {
                        next.delete(element)
                    }
                    return next
                })
            }

            const observer = new IntersectionObserver(
                (entries) => {
                    const latest = entries[entries.length - 1]
                    if (settleTimer) clearTimeout(settleTimer)
                    settleTimer = setTimeout(
                        () => commitVisibility(latest.isIntersecting),
                        CART_ACTION_VISIBILITY_SETTLE_MS,
                    )
                },
                {
                    threshold: 0.1,
                },
            )

            observer.observe(element)

            return () => {
                if (settleTimer) clearTimeout(settleTimer)
                observer.disconnect()
                setVisibleCartActionButtons((prev) => {
                    const next = new Set(prev)
                    next.delete(element)
                    return next
                })
            }
        },
        [],
    )

    /**
     * Register an Inline Product section so it can be scrolled to when the user
     * clicks on the product name/variant in the floating Buy Now button.
     */
    const registerInlineProductSection = useCallback(
        (element: HTMLElement | null, linkHash: string) => {
            if (!element) {
                return () => {}
            }

            inlineProductSectionsRef.current.set(element, { element, linkHash })

            return () => {
                inlineProductSectionsRef.current.delete(element)
            }
        },
        [],
    )

    /**
     * Scroll to the nearest Inline Product section that matches the given linkHash.
     * "Nearest" is determined by finding the section that appears first in the DOM
     * (lowest vertical position on the page).
     */
    const scrollToInlineProductSection = useCallback((linkHash: string) => {
        const sections = Array.from(inlineProductSectionsRef.current.values())

        // Filter to only sections that match the linkHash
        const matchingSections = sections.filter(
            (section) => section.linkHash === linkHash,
        )

        if (matchingSections.length === 0) {
            return false
        }

        // Find the section with the smallest top offset (first in the document)
        let targetSection: InlineProductSectionRegistration | null = null
        let minTop = Infinity

        for (const section of matchingSections) {
            const rect = section.element.getBoundingClientRect()
            if (rect.top < minTop) {
                minTop = rect.top
                targetSection = section
            }
        }

        if (!targetSection) {
            return false
        }

        // Use scrollIntoView for reliable cross-device scrolling (works on both mobile and desktop)
        targetSection.element.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        })
        return true
    }, [])

    const contextValue = useMemo(
        () => ({
            isGridExpanded,
            setIsGridExpanded,
            isCartOpen,
            setIsCartOpen,
            activeLPLink,
            activeLPLinkInitialVariantShopifyId,
            activeLPLinkBodyBadgesOverride,
            setActiveLPLink,
            lastOpenedOverlay,
            registerCartActionButton,
            hasVisibleCartActionButtons: visibleCartActionButtons.size > 0,
            selectedCartActionVariantShopifyId,
            setSelectedCartActionVariantShopifyId,
            registerInlineProductSection,
            scrollToInlineProductSection,
            collectionOverrideLinks,
            openCollectionGrid,
        }),
        [
            isGridExpanded,
            isCartOpen,
            setIsCartOpen,
            activeLPLink,
            activeLPLinkInitialVariantShopifyId,
            activeLPLinkBodyBadgesOverride,
            setActiveLPLink,
            lastOpenedOverlay,
            registerCartActionButton,
            visibleCartActionButtons,
            selectedCartActionVariantShopifyId,
            registerInlineProductSection,
            scrollToInlineProductSection,
            collectionOverrideLinks,
        ],
    )

    return (
        <PageContext.Provider value={contextValue}>
            {children}
        </PageContext.Provider>
    )
}
